import { createApp } from "vue";
import App from "./App.vue";
import { registerPlugins } from "@/plugins";
import axios from "axios";
import store from "./store/index.js";
import VueGoogleMaps from '@fawmi/vue-google-maps';

// Components
import ToastBar from "@/components/Toast.vue";

// API services
import {
  tapi,
  public_api,
  c_public_api,
  pos_print_api,
  pdf_gen_api,
} from "./services/api.js";

// Auth helpers
import { isUserLoggedIn } from "./utils/auth.js";

// Styles
import "@unocss/reset/tailwind.css";

const app = createApp(App);

app.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyAT6VQcf9Yj5SwWTiCaxz3GJQdADpGj6yY",
    libraries: "places"
  }
})

// Register plugins
registerPlugins(app);

// Register global components
app.component('ToastBar', ToastBar);

// Global properties
app.config.globalProperties.$tapi = tapi;
app.config.globalProperties.$papi = public_api;
app.config.globalProperties.$capi = c_public_api;
app.config.globalProperties.$ppapi = pos_print_api;
app.config.globalProperties.$pdf_gen_api = pdf_gen_api;
app.config.globalProperties.$isUserLoggedIn = isUserLoggedIn;

// Environment logic
const node_env = process.env.VUE_APP_NODE_ENV || "production";
console.log(node_env, "node_env");

const prefix =
  node_env === "production"
    ? "orobom"
    : node_env === "development"
    ? "oro"
    : "oro";
let paths = "";
console.log(prefix,"prefix",node_env,"node_env")
if (node_env === "production") {
  if (prefix === "orobom") {
    app.config.globalProperties.apiurl =
      "https://oro-jewellery-api.tectalik.com/api/";
    localStorage.setItem(
      "bucketpath",
      "https://d3qfy7irim6pza.cloudfront.net/2023-2024 FINAL IMAGES/"
    );
    paths = "https://d3qfy7irim6pza.cloudfront.net/2023-2024 FINAL IMAGES/";
  } else {
    app.config.globalProperties.apiurl =
      "https://jewellery-tectalik-api.tectalik.com/api/";
    localStorage.setItem(
      "bucketpath",
      `https://devcdn.tectalik.com/jwl_erp/live/${prefix}/`
    );
    paths = `https://devcdn.tectalik.com/jwl_erp/live/${prefix}/`;
  }
  app.config.globalProperties.$imgurl =
    "https://d3qfy7irim6pza.cloudfront.net/bucket" + paths;
} else if (node_env === "development") {
  app.config.globalProperties.apiurl =
    "https://jewellery-tectalik-api-staging.tectalik.com/api/";
  app.config.globalProperties.$imgurl =
    "https://devcdn.tectalik.com/bucket/staging";

  if (prefix.toLowerCase() === "oro") {
    app.config.globalProperties.$imgurl_original = `https://devcdn.tectalik.com/jwl_erp/ORO/`;
  } else {
    app.config.globalProperties.$imgurl_original = `https://devcdn.tectalik.com/jwl_erp/staging/${prefix}/`;
  }
} else {
  app.config.globalProperties.apiurl = "http://localhost:4020/api/";
  app.config.globalProperties.$imgurl =
    "https://devcdn.tectalik.com/bucket/staging";
  app.config.globalProperties.$imgurl_original = `https://devcdn.tectalik.com/jwl_erp/staging/${prefix}/`;
}

// console.log(
//   node_env,
//   "= environment",
//   app.config.globalProperties.$imgurl_original
// );

// Function to resolve image URL by filename
app.config.globalProperties.$resolveImage = (filename = null) => {
  if (!filename) return null;
  const bucketPath = localStorage.getItem("bucketpath");
  if (filename.startsWith("item_img")) {
    return node_env === "production"
      ? bucketPath
      : prefix.toLowerCase() === "oro"
      ? `https://devcdn.tectalik.com/jwl_erp/ORO/`
      : `https://devcdn.tectalik.com/jwl_erp/live/${prefix}/`;
  }

  return node_env === "production"
    ? bucketPath
    : prefix.toLowerCase() === "oro"
    ? `https://devcdn.tectalik.com/jwl_erp/ORO/`
    : `https://devcdn.tectalik.com/jwl_erp/live/${prefix}/`;
};

// Router & Navigation Guard
import router from "./router";

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("TOKEN");
  const login = localStorage.getItem("LOGIN");
  const customerId = localStorage.getItem("CUSTOMERID");
  const isAdmin = localStorage.getItem("isadmin");
  const status = login ?? "Pending";

  // Updated login logic to match AppHeader component
  const isLoggedIn = status === "Success" || (customerId && customerId !== "null" && customerId !== "" && token && token !== "null" && token !== "");
  const isAdminLoggedIn = isAdmin === "true";

  // Auth screens: a logged-in user has no reason to sit on these.
  const isPublicAuthPage = ["/login", "/signup", "/newlogin", "/approvelogin"].includes(to.path);

  // Browsable without logging in. Must stay in sync with publicPaths in
  // src/router/index.js, otherwise one guard bounces what the other allows.
  const isPublicPage =
    isPublicAuthPage || ["/", "/products", "/market"].includes(to.path);

  console.log("Routing Debug:", {
    path: to.path,
    isLoggedIn,
    isAdminLoggedIn,
    status,
    token: token ? "exists" : "missing",
    customerId: customerId ? "exists" : "missing",
  });

  //  Case 1: If regular user is logged in and navigating to auth page → redirect to home
  if (isLoggedIn && isPublicAuthPage) {
    return next("/");
  }

  //  Case 2: If only admin logged in → allow /signup but block /login
  if (!isLoggedIn && isAdminLoggedIn && to.path === "/login") {
    return next("/");
  }

  //  Case 3: If not logged in at all → redirect to login (except public pages)
  if (!isLoggedIn && !isAdminLoggedIn) {
    if (!isPublicPage) {
      return next("/login");
    }
  }

  //  Case 3: Allow navigation
  return next();
});



// Axios interceptor
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("TOKEN");
    config.url = app.config.globalProperties.apiurl + config.url;
    if (token) {
      config.headers.Authorization = "Bearer " + token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// NOTE: Vuetify and the router are already registered inside
// registerPlugins(app) above. Re-registering them here installed Vuetify
// twice, which corrupted the ClickOutside directive
// ("Cannot read properties of undefined (reading 'j')"). Only register the
// store here.
app.use(store);
app.mount("#app");
